// Motions & Voting Panel Component
import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, Loader2, Plus, Check, X 
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const MotionsPanel = ({ meetingId, agendaItems, isOpen, onClose, isLive, boardMembers = [], currentAgendaItemId = null }) => {
  const [motions, setMotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedMotion, setSelectedMotion] = useState(null);
  const [voting, setVoting] = useState(false);
  
  // Vote details state
  const [showVoteDetails, setShowVoteDetails] = useState(false);
  const [voteDetailsMotion, setVoteDetailsMotion] = useState(null);
  const [voteDetails, setVoteDetails] = useState(null);
  const [loadingVotes, setLoadingVotes] = useState(false);
  
  // New motion form - default to current agenda item if available
  const getDefaultMotion = () => ({
    title: '',
    description: '',
    motion_text: '',
    moved_by: '',
    seconded_by: '',
    agenda_item_id: currentAgendaItemId || '',
    vote_type: 'voice',
    is_anonymous: false,
    requires_supermajority: false
  });
  
  const [newMotion, setNewMotion] = useState(getDefaultMotion());
  
  // Update default agenda item when currentAgendaItemId changes
  useEffect(() => {
    if (currentAgendaItemId && !showCreateForm) {
      setNewMotion(prev => ({ ...prev, agenda_item_id: currentAgendaItemId }));
    }
  }, [currentAgendaItemId, showCreateForm]);

  useEffect(() => {
    if (isOpen && meetingId) {
      fetchMotions();
      // Poll for updates every 10 seconds when open (reduced from 2s to avoid rate limits)
      const interval = setInterval(fetchMotions, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen, meetingId]);

  const fetchMotions = async () => {
    try {
      const res = await api.get(`/board/meetings/${meetingId}/motions`);
      setMotions(res.data.motions || []);
    } catch (err) {
      console.error('Failed to fetch motions:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch individual vote details for a motion
  const fetchVoteDetails = async (motion) => {
    setLoadingVotes(true);
    setVoteDetailsMotion(motion);
    setShowVoteDetails(true);
    try {
      const res = await api.get(`/board/meetings/${meetingId}/motions/${motion.id}/votes`);
      setVoteDetails(res.data);
    } catch (err) {
      console.error('Failed to fetch vote details:', err);
      toast.error('Failed to load vote details');
    } finally {
      setLoadingVotes(false);
    }
  };

  const createMotion = async () => {
    if (!newMotion.title.trim()) {
      toast.error('Motion title is required');
      return;
    }
    
    try {
      await api.post(`/board/meetings/${meetingId}/motions`, newMotion);
      toast.success('Motion created');
      setNewMotion(getDefaultMotion());
      setShowCreateForm(false);
      fetchMotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create motion');
    }
  };

  const secondMotion = async (motionId) => {
    try {
      await api.post(`/board/meetings/${meetingId}/motions/${motionId}/second`);
      toast.success('Motion seconded');
      fetchMotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to second motion');
    }
  };

  const openVoting = async (motionId) => {
    try {
      await api.post(`/board/meetings/${meetingId}/motions/${motionId}/open-voting`);
      toast.success('Voting is now open');
      fetchMotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open voting');
    }
  };

  const castVote = async (motionId, vote) => {
    setVoting(true);
    try {
      await api.post(`/board/meetings/${meetingId}/motions/${motionId}/vote`, { vote });
      toast.success(`Vote recorded: ${vote}`);
      fetchMotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record vote');
    } finally {
      setVoting(false);
    }
  };

  const closeVoting = async (motionId) => {
    if (!window.confirm('Are you sure you want to close voting? This will determine the final result.')) {
      return;
    }
    
    try {
      const res = await api.post(`/board/meetings/${meetingId}/motions/${motionId}/close-voting`);
      toast.success(res.data.message);
      fetchMotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close voting');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-700',
      moved: 'bg-blue-100 text-blue-700',
      seconded: 'bg-purple-100 text-purple-700',
      open_for_vote: 'bg-yellow-100 text-yellow-700',
      passed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      tabled: 'bg-gray-100 text-gray-700',
      withdrawn: 'bg-gray-100 text-gray-500'
    };
    const labels = {
      pending: 'Pending',
      moved: 'Moved',
      seconded: 'Seconded',
      open_for_vote: 'Voting Open',
      passed: 'Passed',
      failed: 'Failed',
      tabled: 'Tabled',
      withdrawn: 'Withdrawn'
    };
    return <Badge className={styles[status] || styles.pending}>{labels[status] || status}</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-purple-600" />
            Motions & Voting
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Create Motion Button */}
            {!showCreateForm && (
              <Button onClick={() => setShowCreateForm(true)} className="mb-4 w-fit">
                <Plus className="h-4 w-4 mr-2" />
                New Motion
              </Button>
            )}

            {/* Create Motion Form */}
            {showCreateForm && (
              <div className="bg-purple-50 rounded-lg p-4 mb-4 space-y-3">
                <h4 className="font-medium text-purple-900">New Motion</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Motion Title *</Label>
                    <Input
                      value={newMotion.title}
                      onChange={(e) => setNewMotion(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Approve Q4 Budget"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Motion Text (exact wording)</Label>
                    <Textarea
                      value={newMotion.motion_text}
                      onChange={(e) => setNewMotion(prev => ({ ...prev, motion_text: e.target.value }))}
                      placeholder="I move that..."
                    />
                  </div>
                  <div>
                    <Label>Moved By</Label>
                    <Select 
                      value={newMotion.moved_by}
                      onValueChange={(v) => setNewMotion(prev => ({ ...prev, moved_by: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select mover" />
                      </SelectTrigger>
                      <SelectContent>
                        {boardMembers.map(m => (
                          <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Seconded By</Label>
                    <Select 
                      value={newMotion.seconded_by}
                      onValueChange={(v) => setNewMotion(prev => ({ ...prev, seconded_by: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select seconder" />
                      </SelectTrigger>
                      <SelectContent>
                        {boardMembers.map(m => (
                          <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Related Agenda Item</Label>
                    <Select 
                      value={newMotion.agenda_item_id || 'none'}
                      onValueChange={(v) => setNewMotion(prev => ({ ...prev, agenda_item_id: v === 'none' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Optional" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {agendaItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.sort_order}. {item.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Vote Type</Label>
                    <Select 
                      value={newMotion.vote_type}
                      onValueChange={(v) => setNewMotion(prev => ({ ...prev, vote_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="voice">Voice Vote</SelectItem>
                        <SelectItem value="roll_call">Roll Call</SelectItem>
                        <SelectItem value="ballot">Secret Ballot</SelectItem>
                        <SelectItem value="unanimous_consent">Unanimous Consent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      checked={newMotion.is_anonymous}
                      onChange={(e) => setNewMotion(prev => ({ ...prev, is_anonymous: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-sm">Anonymous Voting</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      checked={newMotion.requires_supermajority}
                      onChange={(e) => setNewMotion(prev => ({ ...prev, requires_supermajority: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-purple-600"
                    />
                    <span className="text-sm">Requires 2/3 Supermajority</span>
                  </label>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={createMotion}>
                    <Plus className="h-4 w-4 mr-1" />
                    Create Motion
                  </Button>
                </div>
              </div>
            )}

            {/* Motions List */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {motions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No motions yet</p>
              ) : (
                motions.map((motion) => (
                  <div 
                    key={motion.id} 
                    className={`border rounded-lg overflow-hidden ${
                      motion.status === 'open_for_vote' ? 'border-yellow-300 ring-2 ring-yellow-100' : 'border-gray-200'
                    }`}
                  >
                    {/* Motion Header */}
                    <div className="p-4 bg-gray-50 flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            Motion {motion.motion_number}: {motion.title}
                          </span>
                          {getStatusBadge(motion.status)}
                        </div>
                        {motion.motion_text && (
                          <p className="text-sm text-gray-600 italic">&ldquo;{motion.motion_text}&rdquo;</p>
                        )}
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          {motion.moved_by_name && <span>Moved by: {motion.moved_by_name}</span>}
                          {motion.seconded_by_name && <span>Seconded by: {motion.seconded_by_name}</span>}
                          {motion.agenda_item_title && <span>Re: {motion.agenda_item_title}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Voting Section - Only show when voting is open */}
                    {motion.status === 'open_for_vote' && (
                      <div className="p-4 bg-yellow-50 border-t border-yellow-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-yellow-800">Voting in Progress</span>
                          <div className="flex gap-2 text-sm">
                            <span className="text-green-600">For: {motion.votes_for}</span>
                            <span className="text-red-600">Against: {motion.votes_against}</span>
                            <span className="text-gray-500">Abstain: {motion.votes_abstain}</span>
                          </div>
                        </div>
                        
                        {/* My Vote */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sm font-medium">Your Vote:</span>
                          {motion.my_vote ? (
                            <Badge className={
                              motion.my_vote === 'for' ? 'bg-green-100 text-green-700' :
                              motion.my_vote === 'against' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {motion.my_vote === 'for' ? 'For' : motion.my_vote === 'against' ? 'Against' : 'Abstain'}
                            </Badge>
                          ) : (
                            <span className="text-gray-500 text-sm">Not voted</span>
                          )}
                        </div>

                        {/* Vote Buttons */}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant={motion.my_vote === 'for' ? 'default' : 'outline'}
                            className={motion.my_vote === 'for' ? 'bg-green-600 hover:bg-green-700' : ''}
                            onClick={() => castVote(motion.id, 'for')}
                            disabled={voting}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            For
                          </Button>
                          <Button 
                            size="sm" 
                            variant={motion.my_vote === 'against' ? 'default' : 'outline'}
                            className={motion.my_vote === 'against' ? 'bg-red-600 hover:bg-red-700' : ''}
                            onClick={() => castVote(motion.id, 'against')}
                            disabled={voting}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Against
                          </Button>
                          <Button 
                            size="sm" 
                            variant={motion.my_vote === 'abstain' ? 'default' : 'outline'}
                            onClick={() => castVote(motion.id, 'abstain')}
                            disabled={voting}
                          >
                            Abstain
                          </Button>
                          <div className="flex-1" />
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-yellow-700 border-yellow-300"
                            onClick={() => closeVoting(motion.id)}
                          >
                            Close Voting
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons for non-voting states */}
                    {motion.status === 'pending' && (
                      <div className="p-3 border-t flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => secondMotion(motion.id)}>
                          Second This Motion
                        </Button>
                      </div>
                    )}
                    
                    {motion.status === 'moved' && (
                      <div className="p-3 border-t flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => secondMotion(motion.id)}>
                          Second This Motion
                        </Button>
                      </div>
                    )}

                    {motion.status === 'seconded' && (
                      <div className="p-3 border-t flex gap-2">
                        <Button size="sm" onClick={() => openVoting(motion.id)}>
                          Open Voting
                        </Button>
                      </div>
                    )}

                    {/* Results for completed motions */}
                    {(motion.status === 'passed' || motion.status === 'failed') && (
                      <div className={`p-3 border-t ${motion.status === 'passed' ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${motion.status === 'passed' ? 'text-green-700' : 'text-red-700'}`}>
                            Motion {motion.status === 'passed' ? 'Passed' : 'Failed'}
                          </span>
                          <div className="flex gap-4 text-sm">
                            <span className="text-green-600">For: {motion.votes_for}</span>
                            <span className="text-red-600">Against: {motion.votes_against}</span>
                            <span className="text-gray-500">Abstain: {motion.votes_abstain}</span>
                          </div>
                        </div>
                        {/* View Vote Details Button */}
                        {(motion.votes_for > 0 || motion.votes_against > 0 || motion.votes_abstain > 0) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-2 text-xs"
                            onClick={() => fetchVoteDetails(motion)}
                            data-testid={`view-votes-${motion.id}`}
                          >
                            View Vote Details
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </DialogContent>
      
      {/* Vote Details Dialog */}
      <Dialog open={showVoteDetails} onOpenChange={setShowVoteDetails}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vote Details</DialogTitle>
          </DialogHeader>
          
          {loadingVotes ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : voteDetails && voteDetailsMotion ? (
            <div className="space-y-4">
              {/* Motion Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-900">{voteDetailsMotion.title}</p>
                <Badge 
                  className={voteDetailsMotion.status === 'passed' ? 'bg-green-100 text-green-700 mt-1' : 'bg-red-100 text-red-700 mt-1'}
                >
                  {voteDetailsMotion.status === 'passed' ? 'Passed' : 'Failed'}
                </Badge>
              </div>
              
              {/* Vote Summary */}
              <div className="flex justify-center gap-6 py-2 border-y">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{voteDetails.votes_for}</p>
                  <p className="text-xs text-gray-500">For</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{voteDetails.votes_against}</p>
                  <p className="text-xs text-gray-500">Against</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-500">{voteDetails.votes_abstain}</p>
                  <p className="text-xs text-gray-500">Abstain</p>
                </div>
              </div>
              
              {/* Anonymous Vote Notice */}
              {voteDetails.anonymous && voteDetails.votes.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <p>This was an anonymous vote.</p>
                  <p>Individual votes are not recorded.</p>
                </div>
              )}
              
              {/* Individual Votes */}
              {voteDetails.votes && voteDetails.votes.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Individual Votes:</p>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {voteDetails.votes.map((vote, idx) => (
                      <div 
                        key={vote.id || idx} 
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50"
                      >
                        <span className="text-sm text-gray-900">{vote.voter_name}</span>
                        <Badge 
                          className={
                            vote.vote === 'for' ? 'bg-green-100 text-green-700' :
                            vote.vote === 'against' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }
                        >
                          {vote.vote === 'for' ? 'For' : vote.vote === 'against' ? 'Against' : 'Abstain'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  
                  {/* Entry Method Note */}
                  {voteDetails.votes.some(v => v.entry_method === 'manual') && (
                    <p className="text-xs text-gray-400 mt-2">
                      * Some votes were entered manually by the meeting secretary.
                    </p>
                  )}
                </div>
              )}
              
              {/* No Votes Case */}
              {(!voteDetails.votes || voteDetails.votes.length === 0) && !voteDetails.anonymous && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No individual votes recorded.
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default MotionsPanel;
